/**
 * Supported locales for Zoey. Mirrors `tools/translation/config.ts` and the
 * Firestore rule allowlist on `member_profiles.locale`.
 */

export const SUPPORTED_LOCALES = [
  "en",
  "en-GB",
  "es",
  "fr",
  "it",
  "de",
  "pt-BR",
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

export const LOCALE_NAMES: Record<Locale, { native: string; english: string }> = {
  "en": { native: "English (US)", english: "English (US)" },
  "en-GB": { native: "English (UK)", english: "English (UK)" },
  "es": { native: "Español", english: "Spanish" },
  "fr": { native: "Français", english: "French" },
  "it": { native: "Italiano", english: "Italian" },
  "de": { native: "Deutsch", english: "German" },
  "pt-BR": { native: "Português (Brasil)", english: "Portuguese (Brazil)" },
};

export function isSupportedLocale(value: string | undefined | null): value is Locale {
  return typeof value === "string"
    && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Map a raw browser/device locale string (e.g. "en-US", "fr-CA") to one of
 * our supported locales. Falls back to base language match
 * ("fr-CA" → "fr"), then to default.
 */
export function normalizeLocale(raw: string | undefined | null): Locale {
  if (!raw) return DEFAULT_LOCALE;
  if (isSupportedLocale(raw)) return raw;
  const base = raw.split("-")[0]?.toLowerCase();
  if (!base) return DEFAULT_LOCALE;
  // Special case: en-* falls back to en (we have both en and en-GB)
  if (base === "en") return "en";
  for (const supported of SUPPORTED_LOCALES) {
    if (supported.split("-")[0]?.toLowerCase() === base) return supported;
  }
  return DEFAULT_LOCALE;
}
